import { useContext, useEffect, useState } from "react";
import {
  MdArrowBack,
  MdArrowForward,
  MdExpandMore,
  MdInventory2,
  MdLocalOffer,
  MdPhotoLibrary,
} from "react-icons/md";
import { AppContext } from "../App";
import {
  CDN_URL,
  callApi,
  formatMoney,
  getInlineLoader,
} from "../Helpers";
import fallbackImage from "../assets/banner-removebg-preview.png";
import Footer from "../ones/Footer";

const PAGE_SIZE = 12;

function formatPrice(price) {
  const parsedPrice = Number.parseFloat(price);

  if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
    return "Quote on request";
  }

  return `TZS ${formatMoney(parsedPrice)}`;
}

export default function ProductCatalogLanding({ uuid }) {
  const appContext = useContext(AppContext);
  const [product, setProduct] = useState(null);
  const [catalogItems, setCatalogItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  async function getProductData() {
    const response = await callApi("get-product-data", { uuid });

    if (response.status === 1) {
      setProduct(response.data);
    } else {
      appContext.tellError("Could not fetch product");
    }
  }

  async function getCatalog({ offset = 0, append = false } = {}) {
    const response = await callApi("get-all-product-catalog", {
      uuid,
      limit: PAGE_SIZE + 1,
      offset,
    });
    const rows = response.status === 1 && Array.isArray(response.data)
      ? response.data
      : [];
    const records = rows.slice(0, PAGE_SIZE);

    setCatalogItems((current) => (append ? [...current, ...records] : records));
    setHasMore(rows.length > PAGE_SIZE);
  }

  async function init() {
    setLoading(true);
    await Promise.all([getProductData(), getCatalog()]);
    setLoading(false);
  }

  async function viewMore() {
    setLoadingMore(true);
    await getCatalog({
      offset: catalogItems.length,
      append: true,
    });
    setLoadingMore(false);
  }

  useEffect(() => {
    init();
  }, [uuid]);

  const heroPhoto = product?.photo ? CDN_URL + product.photo : fallbackImage;

  return (
    <>
      <section className="product-catalog-hero">
        <div className="container">
          <button
            type="button"
            className="product-catalog-back"
            onClick={() => appContext.navTo({ item: "products" })}
          >
            <MdArrowBack size={20} />
            Products
          </button>

          <div className="product-catalog-hero-grid">
            <div className="product-catalog-copy" data-aos="fade-up">
              <p className="home-banner-kicker">Catalog</p>
              <h1 className="product-catalog-title font-bold">
                {product?.title || "Product Catalog"}
              </h1>
              <p className="product-catalog-lead">
                {product?.description ||
                  "Explore the available catalog options for this product."}
              </p>

              <div className="product-catalog-meta">
                <span>
                  <MdInventory2 size={18} />
                  {catalogItems.length} catalog options
                </span>
                <button
                  type="button"
                  className="products-page-btn"
                  onClick={() => appContext.navTo({ item: "contact_us" })}
                >
                  Request a Quote
                  <MdArrowForward size={20} />
                </button>
              </div>
            </div>

            <div className="product-catalog-hero-media" data-aos="fade-left">
              <img
                src={heroPhoto}
                alt={product?.title || "Esmeriser product"}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="product-catalog-section">
        <div className="container">
          <div className="products-page-heading" data-aos="fade-up">
            <p className="home-banner-kicker">Available Options</p>
            <h2 className="home-service-title font-bold">
              {product?.title ? `${product.title} Catalog` : "Product Catalog"}
            </h2>
          </div>

          {loading ? (
            <div className="mSupportLoading" style={{ minHeight: 320 }}>
              {getInlineLoader()}
            </div>
          ) : catalogItems.length > 0 ? (
            <>
              <div className="product-catalog-grid">
                {catalogItems.map((item, index) => (
                  <article
                    className="product-catalog-card"
                    key={item.uuid || item.id}
                    data-aos="fade-up"
                    data-aos-delay={(index % 4) * 70}
                  >
                    <div className="product-catalog-card-media">
                      <img
                        src={item.photo ? CDN_URL + item.photo : fallbackImage}
                        alt={item.title || product?.title || "Catalog item"}
                      />
                      <span className="product-catalog-card-badge">
                        <MdPhotoLibrary size={16} />
                        Catalog
                      </span>
                    </div>

                    <div className="product-catalog-card-body">
                      <div>
                        <h3 className="font-bold">
                          {item.title || "Catalog Option"}
                        </h3>
                        <p>
                          {item.description ||
                            "A branded option available for this product."}
                        </p>
                      </div>

                      <span className="product-catalog-price">
                        <MdLocalOffer size={17} />
                        {formatPrice(item.price)}
                      </span>
                    </div>
                  </article>
                ))}
              </div>

              {hasMore ? (
                <div className="page-view-more-row">
                  <button
                    type="button"
                    className="page-view-more-btn"
                    onClick={viewMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? "Loading..." : "View More"}
                    <MdExpandMore size={22} />
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <div className="home-empty-state">
              <h3 className="font-bold">Catalog is coming soon.</h3>
              <p>Published catalog options for this product will appear here.</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </>
  );
}
